import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, RefreshCw, Bookmark, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { PaywallSheet } from "@/components/PaywallSheet";
import Understand from "@/pages/flow/Understand";
import Clarify from "@/pages/flow/Clarify";
import Analyzing from "@/pages/flow/Analyzing";
import Result from "@/pages/flow/Result";

const stepIndex = { loading_understand: 0, understand: 0, clarify: 1, analyzing: 2, result: 2 };

export default function DecideFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const { t, lang } = useLang();

  const STEPS = [t("flow.stepUnderstand"), t("flow.stepClarify"), t("flow.stepDecide")];

  const [step, setStep] = useState("loading_understand");
  const [understanding, setUnderstanding] = useState(null);
  const [factors, setFactors] = useState([]);
  const [answers, setAnswers] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [error, setError] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const rawInputRef = useRef(location.state?.rawInput || null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const pending = sessionStorage.getItem("decide_pending");
    if (!rawInputRef.current && pending) {
      try {
        const p = JSON.parse(pending);
        rawInputRef.current = p.raw_input;
        setUnderstanding({ title: p.title, summary: p.summary, options: p.options, factors: p.factors, questions: p.questions, is_high_stakes: p.is_high_stakes });
        setFactors(p.factors);
        setAnswers(p.answers || {});
        setAnalysis(p.analysis);
        setStep("result");
        return;
      } catch (e) {}
    }
    if (!rawInputRef.current) { navigate("/app", { replace: true }); return; }
    runUnderstand();
    // eslint-disable-next-line
  }, []);

  const runUnderstand = async () => {
    setStep("loading_understand");
    setError(null);
    try {
      const res = await api.post("/decisions/understand", { raw_input: rawInputRef.current, language: lang });
      setUnderstanding(res.data);
      setFactors(res.data.factors);
      setStep("understand");
    } catch (e) {
      setError("understand");
      setStep("error");
    }
  };

  const runAnalyze = async () => {
    setStep("analyzing");
    setError(null);
    try {
      const payload = {
        raw_input: rawInputRef.current,
        title: understanding.title,
        summary: understanding.summary,
        options: understanding.options,
        factors,
        questions: understanding.questions,
        answers,
        is_high_stakes: understanding.is_high_stakes,
        language: lang,
      };
      const res = await api.post("/decisions/analyze", payload);
      setAnalysis(res.data.analysis);
      setStep("result");
    } catch (e) {
      if (e?.response?.status === 402) { setPaywall(true); setStep("clarify"); return; }
      setError("analyze");
      setStep("error");
    }
  };

  const onConfirmUnderstand = () => {
    if (understanding.questions?.length > 0) setStep("clarify");
    else runAnalyze();
  };

  const buildSavePayload = () => ({
    raw_input: rawInputRef.current,
    title: understanding.title,
    summary: understanding.summary,
    options: understanding.options,
    factors,
    questions: understanding.questions,
    answers,
    analysis,
    is_high_stakes: understanding.is_high_stakes,
  });

  const ensureSaved = async () => {
    if (savedId) return savedId;
    if (!user) {
      sessionStorage.setItem("decide_pending", JSON.stringify(buildSavePayload()));
      sessionStorage.setItem("decide_post_login", "/decide");
      login();
      return null;
    }
    const res = await api.post("/decisions", buildSavePayload());
    setSavedId(res.data.id);
    sessionStorage.removeItem("decide_pending");
    return res.data.id;
  };

  const onMarkNextStep = async () => {
    const id = await ensureSaved();
    if (!id) return;
    try {
      await api.post(`/decisions/${id}/next-step`, { next_step: analysis.next_action });
      toast.success(t("toast.markedNextStep"));
      navigate(`/decisions/${id}`);
    } catch (e) { toast.error(t("toast.couldNotSave")); }
  };

  const onSaveOnly = async () => {
    const id = await ensureSaved();
    if (!id) return;
    toast.success(t("toast.decisionSaved"));
    navigate(`/decisions/${id}`);
  };

  const progress = stepIndex[step] ?? 0;

  return (
    <div className="min-h-screen bg-decide-canvas">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-decide-canvas/85 border-b border-decide-line/70">
        <div className="decide-shell flex items-center justify-between h-14">
          <button data-testid="flow-close" onClick={() => navigate("/app")} aria-label="Close" className="text-decide-slate hover:text-decide-ink">
            <X size={22} />
          </button>
          {step !== "error" && step !== "loading_understand" && (
            <div className="flex items-center gap-2" data-testid="flow-progress">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-semibold uppercase tracking-wider ${i <= progress ? "text-decide-forest" : "text-decide-mute"}`}>{s}</span>
                  {i < STEPS.length - 1 && <span className="text-decide-mute">·</span>}
                </div>
              ))}
            </div>
          )}
          <div className="w-6" />
        </div>
        <div className="h-0.5 bg-decide-line">
          <motion.div className="h-full bg-decide-forest" animate={{ width: `${((progress + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
      </header>

      <main className="decide-shell pb-16 pt-6">
        <AnimatePresence mode="wait">
          {step === "loading_understand" && (
            <motion.div key="lu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex min-h-[60vh] flex-col items-center justify-center gap-5">
              <Logo size="lg" />
              <p className="text-decide-slate text-sm animate-pulse">{t("flow.reading")}</p>
            </motion.div>
          )}

          {step === "understand" && (
            <motion.div key="u" exit={{ opacity: 0, y: -10 }}>
              <Understand understanding={understanding} factors={factors} setFactors={setFactors} onConfirm={onConfirmUnderstand} />
            </motion.div>
          )}

          {step === "clarify" && (
            <motion.div key="c" exit={{ opacity: 0, y: -10 }}>
              <Clarify questions={understanding.questions} answers={answers} setAnswers={setAnswers} onSubmit={runAnalyze} />
            </motion.div>
          )}

          {step === "analyzing" && <motion.div key="a"><Analyzing /></motion.div>}

          {step === "result" && analysis && (
            <motion.div key="r" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Result analysis={analysis} options={understanding.options} decisionId={savedId} title={understanding.title} />

              {analysis.next_action && (
                <div className="mt-8 rounded-3xl border border-decide-forest/30 bg-decide-forestLight p-6" data-testid="next-action-card">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-decide-forest">{t("flow.nextAction")}</p>
                  <p className="mt-2 text-lg font-medium leading-snug text-decide-ink">{analysis.next_action}</p>
                  <button data-testid="mark-next-step-btn" onClick={onMarkNextStep} className="btn-accent mt-5 w-full">
                    <Check size={18} /> {t("flow.markNextStep")}
                  </button>
                  <button data-testid="save-decision-btn" onClick={onSaveOnly} className="btn-secondary mt-2 w-full">
                    <Bookmark size={17} /> {user ? t("flow.saveKeep") : t("flow.saveDecision")}
                  </button>
                </div>
              )}

              {savedId && (
                <button data-testid="schedule-review-btn" onClick={() => setReviewOpen(true)} className="btn-ghost mt-4 w-full">
                  <Clock size={16} /> {t("flow.reviewLater")}
                </button>
              )}

              <button data-testid="new-decision-btn" onClick={() => navigate("/app")} className="btn-ghost mt-3 w-full">{t("flow.startAnother")}</button>
            </motion.div>
          )}

          {step === "error" && (
            <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex min-h-[60vh] flex-col items-center justify-center text-center">
              <h2 className="text-2xl font-medium text-decide-ink">{t("flow.errorTitle")}</h2>
              <p className="mt-2 text-decide-slate">{t("flow.errorSub")}</p>
              <div className="mt-6 flex gap-3">
                <button data-testid="retry-btn" onClick={error === "understand" ? runUnderstand : runAnalyze} className="btn-accent">
                  <RefreshCw size={17} /> {t("flow.tryAgain")}
                </button>
                <button onClick={() => navigate("/app")} className="btn-secondary">{t("flow.editDecision")}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <PaywallSheet open={paywall} onClose={() => setPaywall(false)} reason="limit" />
      <ReviewScheduleSheet open={reviewOpen} onClose={() => setReviewOpen(false)} decisionId={savedId} t={t} />
    </div>
  );
}

function ReviewScheduleSheet({ open, onClose, decisionId, t }) {
  const opts = [
    { key: "1w", label: t("flow.in1w") },
    { key: "1m", label: t("flow.in1m") },
    { key: "3m", label: t("flow.in3m") },
    { key: "6m", label: t("flow.in6m") },
  ];
  const set = async (when) => {
    try {
      await api.post(`/decisions/${decisionId}/review-schedule`, { when });
      toast.success(t("toast.reviewScheduled"));
      onClose();
    } catch (e) { toast.error(t("toast.couldNotSchedule")); }
  };
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-50 bg-black/30" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            className="fixed bottom-0 inset-x-0 z-50 mx-auto max-w-lg bg-decide-canvas rounded-t-3xl border-t border-decide-line px-6 pt-3 pb-8"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}
            data-testid="review-schedule-sheet"
          >
            <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-decide-line" />
            <h3 className="text-xl font-semibold text-decide-ink">{t("flow.reviewWhen")}</h3>
            <p className="mt-1 text-decide-slate text-sm">{t("flow.reviewWhenSub")}</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {opts.map((o) => (
                <button key={o.key} data-testid={`review-${o.key}`} onClick={() => set(o.key)} className="rounded-2xl border border-decide-line bg-decide-surface px-4 py-4 font-medium text-decide-ink hover:border-decide-ink transition-colors">
                  {o.label}
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
